import { useEffect, useState, useCallback } from 'react';
import { adminGet, adminPost } from './adminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Card, GREEN, mono } from './bfsi/bfsiShared';

const STATUS_STYLE = {
  PENDING: { background: '#FDF3E0', color: '#9a6b12' },
  APPROVED: { background: '#E3F1EC', color: GREEN },
  EXECUTED: { background: '#E3F1EC', color: GREEN },
  REJECTED: { background: '#FBEAE5', color: '#B3401F' },
  EXPIRED: { background: '#EFEDE7', color: '#9a9e96' },
};

export function DecisionModal({ req, onClose, onDone }) {
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const decide = async (decision) => {
    setBusy(true);
    try {
      const r = await adminPost(`/approvals/${req.id}/decision`, { decision, password, reason });
      toast.success(decision === 'APPROVE' ? `Approved · request is ${r.status}` : 'Rejected');
      onDone(); onClose();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="decision-modal">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="font-bold mb-1" style={{ color: '#1B1D1C' }}>Decide: {req.action_type}</h3>
        <p className="text-xs mb-4" style={{ color: '#9a9e96' }}>
          Requested by {req.requested_by} · "{req.reason}". E-signature: re-enter your password and give a written reason.
        </p>
        <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>Your password (e-signature)</label>
        <Input data-testid="decision-password-input" type="password" value={password} onChange={e => setPassword(e.target.value)}
          className="bg-white border-[#D9D6CD] mb-3" />
        <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>Reason (min 5 characters)</label>
        <textarea data-testid="decision-reason-input" value={reason} onChange={e => setReason(e.target.value)} rows={2}
          className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: '#D9D6CD' }} />
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} data-testid="decision-cancel-btn">Cancel</Button>
          <Button variant="outline" disabled={busy || !password || reason.trim().length < 5} data-testid="decision-reject-btn"
            onClick={() => decide('REJECT')} className="border-[#B3401F] text-[#B3401F]">Reject</Button>
          <Button disabled={busy || !password || reason.trim().length < 5} data-testid="decision-approve-btn"
            onClick={() => decide('APPROVE')} className="text-white" style={{ background: GREEN }}>Approve</Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminApprovalsPage() {
  const [reqs, setReqs] = useState([]);
  const [status, setStatus] = useState('');
  const [modal, setModal] = useState(null);
  const [code, setCode] = useState(null);

  const load = useCallback(() => {
    adminGet(`/approvals${status ? `?status=${status}` : ''}`).then(setReqs).catch(e => toast.error(e.message));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const viewCode = async (req) => {
    try {
      const r = await adminGet(`/offline-codes/${req.id}`);
      setCode({ req_id: req.id, ...r });
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-approvals-page">
      <h1 className="text-4xl font-bold mb-1" style={{ color: '#1B1D1C' }}>Approvals</h1>
      <p className="text-sm mb-6" style={{ color: '#9a9e96' }}>
        Maker-checker requests. Requesters can never approve their own request; approvals need an e-signature.
      </p>
      <div className="flex gap-1 mb-5">
        {['', 'PENDING', 'EXECUTED', 'REJECTED', 'EXPIRED'].map(s => (
          <button key={s || 'all'} data-testid={`approvals-filter-${(s || 'all').toLowerCase()}`}
            onClick={() => setStatus(s)}
            className="px-3 py-1.5 text-sm rounded-md font-medium"
            style={status === s ? { background: GREEN, color: 'white' } : { color: '#6b7069' }}>
            {s || 'All'}
          </button>
        ))}
      </div>
      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase" style={{ color: '#9a9e96' }}>
              {['Action', 'Target', 'Requested by', 'Reason', 'Decisions', 'Status', ''].map(h =>
                <th key={h} className="px-4 py-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {reqs.length === 0 && <tr><td colSpan="7" className="px-4 py-8 text-center" style={{ color: '#9a9e96' }}>No requests</td></tr>}
            {reqs.map(q => (
              <tr key={q.id} className="border-t align-top" style={{ borderColor: '#F0EEE8' }} data-testid={`approval-row-${q.id}`}>
                <td className="px-4 py-3" style={mono}>{q.action_type}</td>
                <td className="px-4 py-3 text-xs" style={mono}>{q.target_type} {q.target_id ? `· ${q.target_id.slice(0, 8)}…` : ''}</td>
                <td className="px-4 py-3">{q.requested_by}</td>
                <td className="px-4 py-3 text-xs max-w-[180px]" style={{ color: '#6b7069' }}>{q.reason}</td>
                <td className="px-4 py-3 text-xs" style={{ color: '#6b7069' }}>
                  {q.decisions.map((d, i) => <div key={`${d.by}-${d.at || d.decision}-${i}`}>{d.decision} · {d.by}</div>)}
                  {q.decisions.length === 0 && '—'}
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={STATUS_STYLE[q.status]}>{q.status}</span>
                </td>
                <td className="px-4 py-3">
                  {q.status === 'PENDING' && (
                    <Button size="sm" data-testid={`approval-decide-btn-${q.id}`} onClick={() => setModal(q)}
                      className="h-7 text-xs text-white" style={{ background: GREEN }}>Decide</Button>
                  )}
                  {q.status === 'EXECUTED' && q.action_type === 'OFFLINE_CODE_ISSUE' && (
                    <Button size="sm" variant="outline" data-testid={`approval-viewcode-btn-${q.id}`} onClick={() => viewCode(q)}
                      className="h-7 text-xs">View code</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {modal && <DecisionModal req={modal} onClose={() => setModal(null)} onDone={load} />}
      {code && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="offline-code-modal">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm text-center">
            <h3 className="font-bold mb-2" style={{ color: '#1B1D1C' }}>One-time offline code</h3>
            <div className="text-3xl font-bold tracking-widest my-4" style={{ ...mono, color: GREEN }} data-testid="offline-code-value">{code.code}</div>
            <p className="text-xs mb-4" style={{ color: '#B3401F' }}>
              Shown once — only its hash is kept. Expires {new Date(code.expires_at).toLocaleTimeString()}.
            </p>
            <Button onClick={() => setCode(null)} data-testid="offline-code-close-btn" className="text-white" style={{ background: GREEN }}>Done</Button>
          </div>
        </div>
      )}
    </div>
  );
}
